import React, { useId, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import DataTable from './DataTable';
import { chartToPngBlob } from './chartPng';
import { saveBlob } from './download';

/**
 * One chart with its text alternative and its image download.
 *
 * The chart is exposed to assistive technology as a picture (`role="img"`
 * named by the heading and described by `summary`), and the "Show data"
 * toggle reveals a real table of the same figures, which is what a screen
 * reader user, a keyboard user who cannot judge a bar, and anyone who wants
 * the exact number read instead. Both buttons carry the chart's title in
 * their accessible name, after the visible words, so a page with several
 * charts has distinguishable controls (WCAG 2.5.3 holds: the visible text
 * starts the name).
 */
function ChartCard({ title, summary, table, filename, children }) {
    const id = useId().replace(/:/g, '');
    const chartRef = useRef(null);
    const [showData, setShowData] = useState(false);
    const [error, setError] = useState('');

    const handlePng = async () => {
        setError('');
        try {
            saveBlob(await chartToPngBlob(chartRef.current, title), filename);
        } catch (_error) {
            setError(`Could not save the ${title} chart as an image.`);
        }
    };

    return (
        <section
            aria-labelledby={`${id}-title`}
            className="afe-chart-card mb-4"
        >
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h3 id={`${id}-title`} className="h6 mb-0">
                    {title}
                </h3>
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        aria-expanded={showData}
                        aria-controls={`${id}-data`}
                        onClick={() => setShowData((open) => !open)}
                    >
                        {showData ? 'Hide data' : 'Show data'}
                        <span className="visually-hidden"> for {title}</span>
                    </Button>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handlePng}
                    >
                        Download PNG
                        <span className="visually-hidden">
                            {' '}
                            of {title} chart
                        </span>
                    </Button>
                </div>
            </div>
            <p id={`${id}-summary`} className="text-muted small mb-2">
                {summary}
            </p>
            {error && (
                <Alert variant="danger" className="py-2">
                    {error}
                </Alert>
            )}
            <div
                ref={chartRef}
                className="afe-chart"
                role="img"
                aria-labelledby={`${id}-title`}
                aria-describedby={`${id}-summary`}
            >
                {children}
            </div>
            <div id={`${id}-data`}>
                {showData && (
                    <DataTable
                        caption={`${title}: figures`}
                        corner={table.corner}
                        columns={table.columns}
                        rows={table.rows}
                    />
                )}
            </div>
        </section>
    );
}

export default ChartCard;
